import React, { useEffect, useRef, useState } from 'react';
import FitTable from '../components/FitTable';
import { useAuth0 } from '@auth0/auth0-react';

const APIURL = process.env.REACT_APP_API_URL;
const DOMAIN = 'bgagolf.ca';

interface UserProfile {
  user_id: string;
  username: string;
  display_name: string | null;
  personal_email: string;
  player_id: number | null;
  whatsapp_number?: string | null;
}

interface EmailAlias {
  alias_id: number;
  alias_address: string;
  label: string | null;
  forward_to: string;
  is_active: boolean;
}

export default function Profile() {
  const { getAccessTokenSilently, user: auth0User } = useAuth0();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [aliases, setAliases] = useState<EmailAlias[]>([]);

  // Profile form state
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [personalEmail, setPersonalEmail] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');

  // Alias form state
  const [newPrefix, setNewPrefix] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newForwardTo, setNewForwardTo] = useState('');
  const [aliasError, setAliasError] = useState('');
  const [creatingAlias, setCreatingAlias] = useState(false);

  const usernameCheckTimer = useRef<ReturnType<typeof setTimeout>>();

  async function token() {
    return getAccessTokenSilently();
  }

  async function loadAll() {
    const t = await token();
    const headers = { Authorization: `Bearer ${t}` };

    const [profileRes, aliasRes] = await Promise.all([
      fetch(`${APIURL}/profile`, { headers }),
      fetch(`${APIURL}/email-aliases`, { headers }),
    ]);

    if (profileRes.ok) {
      const p: UserProfile = await profileRes.json();
      setProfile(p);
      setUsername(p.username);
      const parts = (p.display_name || '').split(' ');
      setFirstName(parts[0] || '');
      setLastName(parts.slice(1).join(' ') || '');
      setPersonalEmail(p.personal_email);
      setWhatsappNumber(p.whatsapp_number || '');
    } else {
      // Pre-fill from Auth0 on first visit
      setUsername(auth0User?.nickname || auth0User?.email?.split('@')[0] || '');
      setFirstName(auth0User?.given_name || auth0User?.name?.split(' ')[0] || '');
      setLastName(auth0User?.family_name || auth0User?.name?.split(' ').slice(1).join(' ') || '');
      setPersonalEmail(auth0User?.email || '');
    }

    if (aliasRes.ok) setAliases(await aliasRes.json());
  }

  useEffect(() => { loadAll(); }, []);

  function handleUsernameChange(val: string) {
    const safe = val.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    setUsername(safe);
    setUsernameStatus('idle');
    clearTimeout(usernameCheckTimer.current);
    if (!safe || safe === profile?.username) return;
    setUsernameStatus('checking');
    usernameCheckTimer.current = setTimeout(async () => {
      const t = await token();
      const res = await fetch(`${APIURL}/profile/check-username?username=${safe}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const data = await res.json();
      setUsernameStatus(data.available ? 'available' : 'taken');
    }, 500);
  }

  async function saveProfile() {
    if (usernameStatus === 'taken') return;
    setSaving(true);
    setProfileMsg('');
    const t = await token();
    const displayName = [firstName, lastName].filter(Boolean).join(' ');

    try {
      const method = profile ? 'PUT' : 'POST';
      const res = await fetch(`${APIURL}/profile`, {
        method,
        headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          display_name: displayName,
          personal_email: personalEmail,
          ...(profile ? { whatsapp_number: whatsappNumber } : {}),
        }),
      });

      if (!res.ok) throw new Error((await res.json()).error || 'Save failed');

      const updated = await res.json();
      setProfile(updated);
      setProfileMsg('Profile saved.');

      // Reload aliases — username change may have added a new alias
      const aliasRes = await fetch(`${APIURL}/email-aliases`, { headers: { Authorization: `Bearer ${t}` } });
      if (aliasRes.ok) setAliases(await aliasRes.json());
    } catch (err: any) {
      setProfileMsg(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function createAlias() {
    setAliasError('');
    if (!newPrefix.trim()) return setAliasError('Alias prefix is required');
    if (!newForwardTo.trim()) return setAliasError('Forward-to email is required');
    setCreatingAlias(true);
    try {
      const t = await token();
      const res = await fetch(`${APIURL}/email-aliases`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ alias_prefix: newPrefix.trim(), label: newLabel.trim() || null, forward_to: newForwardTo.trim() }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed');
      const created = await res.json();
      setAliases((prev) => [created, ...prev]);
      setNewPrefix(''); setNewLabel(''); setNewForwardTo('');
    } catch (err: any) {
      setAliasError(err.message);
    } finally {
      setCreatingAlias(false);
    }
  }

  async function toggleAlias(alias: EmailAlias) {
    const t = await token();
    const res = await fetch(`${APIURL}/email-aliases/${alias.alias_id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !alias.is_active }),
    });
    const updated = await res.json();
    setAliases((prev) => prev.map((a) => (a.alias_id === alias.alias_id ? updated : a)));
  }

  async function deleteAlias(id: number, address: string) {
    if (!window.confirm(`Delete alias ${address}?`)) return;
    const t = await token();
    await fetch(`${APIURL}/email-aliases/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${t}` } });
    setAliases((prev) => prev.filter((a) => a.alias_id !== id));
  }

  const usernameHint =
    usernameStatus === 'checking' ? '⟳ Checking...' :
    usernameStatus === 'available' ? '✓ Available' :
    usernameStatus === 'taken' ? '✗ Already taken' : '';

  const usernameHintColor =
    usernameStatus === 'available' ? '#27ae60' :
    usernameStatus === 'taken' ? '#c0392b' : '#888';

  return (
    <div style={{ padding: '20px', maxWidth: '820px', margin: '0 auto' }}>
      <h2>My Profile</h2>

      {/* ── Account Details ── */}
      <div style={card}>
        <h3 style={cardHeading}>Account Details</h3>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <label style={label}>First Name</label>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} style={input} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={label}>Last Name</label>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} style={input} />
          </div>
        </div>

        <label style={label}>Username</label>
        <div style={{ marginBottom: '4px' }}>
          <input
            value={username}
            onChange={(e) => handleUsernameChange(e.target.value)}
            style={{ ...input, width: '180px' }}
            placeholder="e.g. twong60"
          />
        </div>
        {usernameHint && <p style={{ fontSize: '12px', color: usernameHintColor, margin: '0 0 12px 0' }}>{usernameHint}</p>}

        <label style={label}>Personal Email</label>
        <input
          value={personalEmail}
          onChange={(e) => setPersonalEmail(e.target.value)}
          type="email"
          style={{ ...input, marginBottom: '16px' }}
          placeholder="you@gmail.com"
        />

        <label style={label}>WhatsApp number</label>
        <input
          value={whatsappNumber}
          onChange={(e) => setWhatsappNumber(e.target.value)}
          type="tel"
          inputMode="tel"
          style={{ ...input, width: '220px', marginBottom: '4px' }}
          placeholder="+1 514 555 0123"
        />
        <p style={{ fontSize: '12px', color: '#888', margin: '0 0 16px 0' }}>
          With the country code. Used to message you when a scorecard photo you uploaded needs your review.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={saveProfile} disabled={saving || usernameStatus === 'taken'} style={primaryBtn}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          {profileMsg && (
            <span style={{ fontSize: '13px', color: profileMsg === 'Profile saved.' ? '#27ae60' : '#c0392b' }}>
              {profileMsg}
            </span>
          )}
        </div>

        {profile && username !== profile.username && (
          <p style={{ fontSize: '12px', color: '#888', marginTop: '10px' }}>
            Changing your username will create <code>{username}@{DOMAIN}</code> and keep{' '}
            <code>{profile.username}@{DOMAIN}</code> active until you delete it.
          </p>
        )}
      </div>

      {/* ── Email Aliases ── */}
      <div style={card}>
        <h3 style={cardHeading}>Email Aliases</h3>
        <p style={{ color: '#666', fontSize: '13px', marginTop: '-8px', marginBottom: '16px' }}>
          The following email aliases can be used to create accounts for golf reservations.
          All reservations made under these email aliases will be picked up and made available
          in the list of reservations.
        </p>

        {aliases.length > 0 && (
          <FitTable style={{ marginBottom: '16px' }}>
          <table className="tbl tbl--plain">
            <thead>
              <tr>
                <th>Alias</th>
                <th>Label</th>
                <th>Forwards To</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {aliases.map((a) => (
                <tr key={a.alias_id}>
                  <td><code>{a.alias_address}</code></td>
                  <td>{a.label || <span style={{ color: '#aaa' }}>—</span>}</td>
                  <td>{a.forward_to}</td>
                  <td>
                    <span style={{ color: a.is_active ? '#27ae60' : '#999' }}>
                      {a.is_active ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => toggleAlias(a)} style={smallBtn}>{a.is_active ? 'Pause' : 'Resume'}</button>
                    <button onClick={() => deleteAlias(a.alias_id, a.alias_address)} style={{ ...smallBtn, background: '#e74c3c' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </FitTable>
        )}

        <div style={{ borderTop: aliases.length > 0 ? '1px solid #eee' : 'none', paddingTop: aliases.length > 0 ? '14px' : '0' }}>
          <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Add an alias</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden' }}>
              <input
                value={newPrefix}
                onChange={(e) => setNewPrefix(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                placeholder="alias-name"
                style={{ padding: '7px 10px', border: 'none', outline: 'none', width: '130px' }}
              />
              <span style={{ padding: '7px 10px', background: '#f0f0f0', color: '#555', whiteSpace: 'nowrap' }}>@{DOMAIN}</span>
            </div>
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Label (optional)" style={input} />
            <input value={newForwardTo} onChange={(e) => setNewForwardTo(e.target.value)} placeholder="Forward to" type="email" style={input} />
            <button onClick={createAlias} disabled={creatingAlias} style={primaryBtn}>
              {creatingAlias ? 'Creating...' : 'Add Alias'}
            </button>
          </div>
          {aliasError && <p style={{ color: '#c0392b', fontSize: '13px', marginTop: '6px' }}>{aliasError}</p>}
        </div>
      </div>
    </div>
  );
}

const card: React.CSSProperties = { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px', marginBottom: '20px' };
const cardHeading: React.CSSProperties = { margin: '0 0 16px 0', fontSize: '16px' };
const label: React.CSSProperties = { display: 'block', fontSize: '13px', fontWeight: 600, color: '#444', marginBottom: '4px' };
const input: React.CSSProperties = { padding: '7px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', width: '100%', boxSizing: 'border-box' };
const primaryBtn: React.CSSProperties = { padding: '7px 14px', background: '#2980b9', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' };
const smallBtn: React.CSSProperties = { padding: '0.25em 0.6em', background: '#555', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.86em' };
